import React, { useCallback, useState } from 'react';
import {
  ArrayField,
  composeSyncValidators,
  FieldTitle,
  Identifier,
  InputHelperText,
  InputProps,
  isRequired,
  RecordContextProvider,
  SingleFieldList,
  useGetValidationErrorMessage,
} from 'react-admin';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  FormLabel,
  FormHelperText,
} from '@mui/material';
import { useFieldArray, useFormContext } from 'react-hook-form';

import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

import ReferenceEntityField from './ReferenceEntityField';
import { EntityTypes } from '../constants/entityTypes';
import EntitySelectionDialog from '../components/entities/EntitySelectionDialog';
import EntityDisplayNameField from './EntityDisplayNameField';

type Entity = { id: Identifier; type: EntityTypes }

type FormValues = {
  [key: string]: Entity[];
};

const ReferenceEntitiesInput = (props: InputProps & { allowedTypes?: EntityTypes[] }) => {
  const {
    label,
    source,
    validate,
    helperText,
    allowedTypes,
  } = props;

  const {
    getFieldState,
    formState,
    control,
    getValues,
  } = useFormContext();

  const sanitizedValidate = Array.isArray(validate)
    ? composeSyncValidators(validate)
    : validate;
  const getValidationErrorMessage = useGetValidationErrorMessage();

  const { append, fields, remove } = useFieldArray<FormValues, any, 'key'>({
    control,
    name: source,
    keyName: 'key',
    rules: {
      validate: async (value) => {
        if (!sanitizedValidate) return true;
        const error = await sanitizedValidate(
          value,
          getValues(),
          props,
        );

        if (!error) return true;
        return getValidationErrorMessage(error);
      },
    },
  });

  const { error } = getFieldState(source, formState);

  const [open, setOpen] = useState(false);

  const handleStartEdit = useCallback(() => {
    setOpen(true);
  }, []);
  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleSelect = useCallback((
    data: { id: Identifier; type: EntityTypes },
    wasSelected: boolean,
  ) => {
    if (wasSelected) {
      const index = fields.findIndex((field) => field.id === data.id);
      remove(index);
    } else {
      append(data);
    }
  }, [append, fields, remove]);

  const handleClear = useCallback(() => {
    const fieldsLength = fields.length;
    for (let index = 0; index < fieldsLength; index) {
      remove(0);
      index += 1;
    }
  }, [fields.length, remove]);

  const renderHelperText = helperText !== false;

  return (
    <>
      <FormControl
        fullWidth
        variant="standard"
        error={!!error}
      >
        <FormLabel>
          <FieldTitle
            label={label}
            isRequired={isRequired(validate)}
          />
        </FormLabel>
        <Box display="flex" flexDirection="column">
          <Box display="flex" flexDirection="row" gap={2} alignItems="center">
            {fields.length > 0 && (
              <RecordContextProvider value={{ [source]: fields }}>
                <Box display="flex" flexDirection="column">
                  <ArrayField source={source}>
                    <SingleFieldList linkType={false} flexDirection="column">
                      <ReferenceEntityField source="" linkType={false} emptyText="">
                        <EntityDisplayNameField />
                      </ReferenceEntityField>
                    </SingleFieldList>
                  </ArrayField>
                </Box>
              </RecordContextProvider>
            )}
            <Box>
              {fields.length > 0 ? (
                <>
                  <IconButton size="small" onClick={handleStartEdit} color="primary">
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" onClick={handleClear} color="warning">
                    <HighlightOffIcon />
                  </IconButton>
                </>
              ) : (
                <Button variant="text" size="small" onClick={handleStartEdit} color="primary" endIcon={<AddIcon />}>
                  Select entities
                </Button>
              )}
            </Box>
          </Box>
          {renderHelperText && (
            <FormHelperText error={!!error}>
              <InputHelperText
                error={error?.root?.message ?? error?.message}
                helperText={helperText}
              />
            </FormHelperText>
          )}
        </Box>
      </FormControl>
      <EntitySelectionDialog
        currentValue={fields.map((value) => value.id)}
        open={open}
        multiple
        handleClose={handleClose}
        handleSelect={handleSelect}
        label={label}
        allowedTypes={allowedTypes}
      />
    </>
  );
};

export default ReferenceEntitiesInput;
